import { type ReactElement, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useAuth } from '@/shared/auth/AuthContext';

/**
 * Wraps protected routes.
 *
 * Three states, and only two of them are the obvious ones. Signed in, render
 * the screen; signed out, go to `/login`. The third is `pending` — the probe to
 * `/auth/me` has not answered yet — and the answer to "is this person signed
 * in?" is then not "no" but "not yet". Redirecting during it would send an
 * operator who *is* signed in to the sign-in page and back, which reads as a
 * flicker on every cold load and as a loop on a slow connection.
 *
 * `null` rather than a spinner: the wait is one request long, and a spinner that
 * appears and vanishes inside 100ms is noise. The layout's Suspense boundary
 * covers the screen that follows.
 */
export function AuthGuard({ children }: { readonly children: ReactNode }): ReactElement | null {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'pending') {
    return null;
  }

  if (status === 'anonymous') {
    /* Where they were trying to go, carried through the sign-in and back. The
       service restricts it to a same-site path before acting on it — it arrives
       there from a query string, which anybody can write. */
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }

  return <>{children}</>;
}
